import React, { useState, useEffect, useRef } from 'react';
import { MapPin, X, Save, Trash2, Bell, Plus, List } from 'lucide-react';

const WORKER_URL = 'https://bulgaria-rain-tracker-worker.apigetposttest.workers.dev';

const BulgariaRainTracker = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showLocationsList, setShowLocationsList] = useState(false);
  const [editData, setEditData] = useState({ name: '', notes: '', enableNotifications: false });
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    const loadLocations = async () => {
      if (!isAuthenticated) return;
      
      setLoading(true);
      try {
        const stored = localStorage.getItem('rain_tracker_locations');
        if (stored) {
          setLocations(JSON.parse(stored));
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadLocations();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Load Leaflet CSS and JS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = initMap;
    document.body.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (mapInstanceRef.current && locations.length > 0) {
      updateMarkers();
    }
  }, [locations]);

  // Initialize markers after map is ready and locations are loaded
  useEffect(() => {
    if (mapInstanceRef.current && locations.length > 0 && !loading) {
      updateMarkers();
    }
  }, [mapInstanceRef.current, locations, loading]);

  const initMap = () => {
    if (mapInstanceRef.current) return;

    const L = window.L;
    const map = L.map(mapRef.current, {
      zoomControl: true
    }).setView([42.7339, 25.4858], 7);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    mapInstanceRef.current = map;
    
    // Wait a bit for locations to load, then update markers
    setTimeout(() => {
      if (locations.length > 0) {
        updateMarkers();
      }
    }, 100);
  };

  const updateMarkers = () => {
    if (!mapInstanceRef.current || !window.L) return;

    const L = window.L;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add markers for each location
    locations.forEach(loc => {
      const iconHtml = loc.enableNotifications
        ? `<svg width="32" height="32" viewBox="0 0 24 24" fill="#dc2626" stroke="#dc2626" stroke-width="2">
             <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
             <circle cx="12" cy="10" r="3" fill="white"></circle>
           </svg>`
        : `<svg width="32" height="32" viewBox="0 0 24 24" fill="#2563eb" stroke="#2563eb" stroke-width="2">
             <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
             <circle cx="12" cy="10" r="3" fill="white"></circle>
           </svg>`;

      const customIcon = L.divIcon({
        html: iconHtml,
        className: 'custom-marker',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      const marker = L.marker([loc.lat, loc.lng], { icon: customIcon })
        .addTo(mapInstanceRef.current)
        .on('click', () => openLocationModal(loc));

      markersRef.current.push(marker);
    });
  };

  const handleLogin = () => {
    if (password === 'ssparky') {
      setIsAuthenticated(true);
      localStorage.setItem('rain_tracker_auth', 'true');
    } else {
      alert('Invalid password');
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          addLocation(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          alert('Error getting location: ' + error.message);
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const addLocation = (lat, lng) => {
    const newLocation = {
      id: Date.now().toString(),
      lat,
      lng,
      name: `Location ${locations.length + 1}`,
      notes: '',
      enableNotifications: false,
      createdAt: new Date().toISOString()
    };
    
    const updatedLocations = [...locations, newLocation];
    setLocations(updatedLocations);
    saveLocations(updatedLocations);
    
    // Fly to new location
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([lat, lng], 13);
    }
    
    setSelectedLocation(newLocation);
    setEditData({
      name: newLocation.name,
      notes: newLocation.notes,
      enableNotifications: newLocation.enableNotifications
    });
    setShowModal(true);
  };

  const saveLocations = async (locs) => {
    try {
      localStorage.setItem('rain_tracker_locations', JSON.stringify(locs));
      
      const response = await fetch(`${WORKER_URL}/api/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locations: locs })
      });
      
      if (!response.ok) {
        console.error('Failed to sync with worker');
      }
    } catch (error) {
      console.error('Error saving locations:', error);
    }
  };

  const openLocationModal = (location) => {
    setSelectedLocation(location);
    setEditData({
      name: location.name,
      notes: location.notes,
      enableNotifications: location.enableNotifications
    });
    setShowModal(true);
    
    // Fly to location
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([location.lat, location.lng], 13);
    }
  };

  const saveLocationData = () => {
    const updatedLocations = locations.map(loc =>
      loc.id === selectedLocation.id
        ? { ...loc, ...editData }
        : loc
    );
    setLocations(updatedLocations);
    saveLocations(updatedLocations);
    setShowModal(false);
  };

  const removeLocation = () => {
    const updatedLocations = locations.filter(loc => loc.id !== selectedLocation.id);
    setLocations(updatedLocations);
    saveLocations(updatedLocations);
    setShowModal(false);
  };

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #eff6ff, #dbeafe)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          padding: '2rem',
          width: '100%',
          maxWidth: '28rem'
        }}>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            Rain Tracker
          </h1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="Enter password"
              style={{
                width: '-webkit-fill-available',
                padding: '0.75rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <button
              onClick={handleLogin}
              style={{
                width: '100%',
                background: '#2563eb',
                color: 'white',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '1rem'
              }}
            >
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: '100vh',
      height: '100dvh', // Dynamic viewport height for mobile
      width: '100vw',
      overflow: 'hidden'
    }}>
      {/* Map Container */}
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />

      {/* Locations List Button */}
      <button
        onClick={() => setShowLocationsList(!showLocationsList)}
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '3rem',
          height: '3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          zIndex: 1000
        }}
      >
        <List size={24} color="#2563eb" />
      </button>

      {/* Add Location Button */}
      <button
        onClick={getCurrentLocation}
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#2563eb',
          color: 'white',
          padding: '1rem 2rem',
          borderRadius: '2rem',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontWeight: '600',
          fontSize: '1rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
          transition: 'all 0.2s',
          maxWidth: 'calc(100vw - 2rem)',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)';
          e.currentTarget.style.boxShadow = '0 25px 30px -5px rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
          e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.3)';
        }}
      >
        <Plus size={20} />
        <span style={{ display: 'inline' }}>Add New Pin</span>
      </button>

      {/* Locations List Sidebar */}
      {showLocationsList && (
        <div style={{
          position: 'absolute',
          top: '5rem',
          right: '1rem',
          width: 'min(20rem, calc(100vw - 2rem))',
          maxHeight: 'calc(100vh - 6rem)',
          maxHeight: 'calc(100dvh - 6rem)',
          background: 'white',
          borderRadius: '1rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid #e5e7eb',
            fontWeight: 'bold',
            color: '#1f2937'
          }}>
            Saved Locations ({locations.length})
          </div>
          <div style={{
            overflowY: 'auto',
            flex: 1,
            padding: '0.5rem'
          }}>
            {locations.length === 0 ? (
              <p style={{
                textAlign: 'center',
                color: '#6b7280',
                padding: '2rem 1rem'
              }}>
                No locations yet
              </p>
            ) : (
              locations.map((loc) => (
                <div
                  key={loc.id}
                  onClick={() => {
                    openLocationModal(loc);
                    setShowLocationsList(false);
                  }}
                  style={{
                    padding: '0.75rem',
                    margin: '0.25rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#60a5fa';
                    e.currentTarget.style.background = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '600',
                        color: '#1f2937',
                        fontSize: '0.875rem'
                      }}>
                        {loc.name}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginTop: '0.25rem'
                      }}>
                        {loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}
                      </div>
                    </div>
                    {loc.enableNotifications && (
                      <Bell size={14} color="#2563eb" fill="#2563eb" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Location Details Modal */}
      {showModal && selectedLocation && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '28rem',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ padding: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Location Details
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: 0
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.25rem'
                  }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    style={{
                      width: '-webkit-fill-available',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.25rem'
                  }}>
                    Coordinates
                  </label>
                  <input
                    type="text"
                    value={`${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`}
                    disabled
                    style={{
                      width: '-webkit-fill-available',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      background: '#f9fafb',
                      color: '#4b5563',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.25rem'
                  }}>
                    Notes
                  </label>
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    rows={4}
                    placeholder="Add notes about this location..."
                    style={{
                      width: '-webkit-fill-available',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: '#eff6ff',
                  borderRadius: '0.5rem'
                }}>
                  <input
                    type="checkbox"
                    id="notifications"
                    checked={editData.enableNotifications}
                    onChange={(e) => setEditData({ ...editData, enableNotifications: e.target.checked })}
                    style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      cursor: 'pointer'
                    }}
                  />
                  <label
                    htmlFor="notifications"
                    style={{
                      fontSize: '0.875rem',
                      color: '#374151',
                      flex: 1,
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>Enable rain notifications</div>
                    <div style={{ color: '#6b7280' }}>Daily check for rain in 7-day forecast</div>
                  </label>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  paddingTop: '1rem'
                }}>
                  <button
                    onClick={saveLocationData}
                    style={{
                      flex: 1,
                      background: '#2563eb',
                      color: 'white',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontWeight: '500',
                      fontSize: '1rem'
                    }}
                  >
                    <Save size={18} />
                    Save
                  </button>
                  <button
                    onClick={removeLocation}
                    style={{
                      flex: 1,
                      background: '#dc2626',
                      color: 'white',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontWeight: '500',
                      fontSize: '1rem'
                    }}
                  >
                    <Trash2 size={18} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for custom markers */}
      <style>{`
        .custom-marker {
          background: none !important;
          border: none !important;
        }
        .leaflet-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        }
        /* Fix mobile viewport */
        body, html {
          margin: 0;
          padding: 0;
          overflow: hidden;
          height: 100vh;
          height: 100dvh;
          width: 100vw;
          position: fixed;
        }
        #root {
          height: 100vh;
          height: 100dvh;
          width: 100vw;
        }
        /* Responsive text for mobile */
        @media (max-width: 640px) {
          .custom-marker svg {
            width: 28px !important;
            height: 28px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BulgariaRainTracker;